import {
  Box,
  HStack,
  Image,
  Link,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal
} from '@chakra-ui/react';
import { AppleLogoIcon, GooglePlayLogoIcon, CaretDownIcon } from '@phosphor-icons/react';
import { DOWNLOAD_LINKS } from '@/travo/config';
import { useCopy, useLocale, type Locale } from '@/travo/i18n';
import { PILL_BUTTON } from '@/travo/theme/constants';
import iosBadgeEn from '@/travo/assets/images/shared/ios-badge-en.svg';
import playBadgeEn from '@/travo/assets/images/shared/google-play-badge.svg';
import iosBadgeZhTW from '@/travo/assets/images/shared/ios-badge-zh-tw.svg';
import playBadgeZhTW from '@/travo/assets/images/shared/google-play-badge-zh-tw.png';
import iosBadgeJa from '@/travo/assets/images/shared/ios-badge-ja.svg';
import playBadgeJa from '@/travo/assets/images/shared/google-play-badge-ja.png';
import iosBadgeKo from '@/travo/assets/images/shared/ios-badge-ko.svg';
import playBadgeKo from '@/travo/assets/images/shared/google-play-badge-ko.png';
import iosBadgeTh from '@/travo/assets/images/shared/ios-badge-th.svg';
import playBadgeTh from '@/travo/assets/images/shared/google-play-badge-th.png';

/**
 * Official store badges per locale (Apple Marketing Tools, Google Play badge generator).
 */
const BADGES: Record<Locale, { appStore: string; playStore: string }> = {
  en: { appStore: iosBadgeEn, playStore: playBadgeEn },
  'zh-TW': { appStore: iosBadgeZhTW, playStore: playBadgeZhTW },
  ja: { appStore: iosBadgeJa, playStore: playBadgeJa },
  ko: { appStore: iosBadgeKo, playStore: playBadgeKo },
  th: { appStore: iosBadgeTh, playStore: playBadgeTh }
};

/** App Store page in the locale's storefront. */
const APP_STORE_URLS: Record<Locale, string> = {
  en: DOWNLOAD_LINKS.appStore,
  'zh-TW': DOWNLOAD_LINKS.appStoreTW,
  ja: DOWNLOAD_LINKS.appStoreJP,
  ko: DOWNLOAD_LINKS.appStoreKR,
  th: DOWNLOAD_LINKS.appStoreTH
};

interface DownloadButtonsProps {
  variant?: 'badge' | 'nav-menu';
  /** Badge height in px. */
  size?: number;
}

const DownloadButtons = ({ variant = 'badge', size = 52 }: DownloadButtonsProps) => {
  const copy = useCopy();
  const locale = useLocale();
  const badges = BADGES[locale];
  const appStoreUrl = APP_STORE_URLS[locale];

  if (variant === 'nav-menu') {
    return (
      <MenuRoot positioning={{ placement: 'bottom-end' }}>
        <MenuTrigger asChild>
          <Box as="button" {...PILL_BUTTON} fontSize="15px">
            {copy.nav.download}
            <CaretDownIcon size={14} weight="bold" />
          </Box>
        </MenuTrigger>
        <Portal>
          <MenuPositioner>
            <MenuContent minW="220px" borderRadius="16px" p="8px" bg="card" border="2px solid" borderColor="ink">
              <MenuItem value="app-store" asChild borderRadius="10px" cursor="pointer" p="8px 4px">
                <Link href={appStoreUrl} target="_blank" rel="noopener noreferrer" gap="12px" w="100%" px="14px" py="14px" color="ink">
                  <AppleLogoIcon size={22} weight="fill" />
                  {copy.download.ios}
                </Link>
              </MenuItem>
              <MenuItem value="google-play" asChild borderRadius="10px" cursor="pointer" p="8px 4px">
                <Link href={DOWNLOAD_LINKS.playStore} target="_blank" rel="noopener noreferrer" gap="12px" w="100%" px="14px" py="14px" color="ink">
                  <GooglePlayLogoIcon size={22} weight="fill" />
                  {copy.download.android}
                </Link>
              </MenuItem>
            </MenuContent>
          </MenuPositioner>
        </Portal>
      </MenuRoot>
    );
  }

  return (
    <HStack gap="14px" flexWrap="wrap">
      <Link href={appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={copy.download.appStore}>
        <Image src={badges.appStore} alt="" h={`${size}px`} transition="transform 0.2s" _hover={{ transform: 'scale(1.04)' }} />
      </Link>
      <Link href={DOWNLOAD_LINKS.playStore} target="_blank" rel="noopener noreferrer" aria-label={copy.download.playStore}>
        <Image src={badges.playStore} alt="" h={`${size}px`} transition="transform 0.2s" _hover={{ transform: 'scale(1.04)' }} />
      </Link>
    </HStack>
  );
};

export default DownloadButtons;
